/**
 * useStreamDraft — hook que consume el SSE stream del SectionDraftAgent
 * y mantiene el estado UI (steps, draft final, métricas, errores).
 *
 * Patrón:
 *
 *   const { phase, steps, result, error, start, abort } = useStreamDraft()
 *
 *   <button onClick={() => start({artifactId, sectionId})}>Generar</button>
 *
 *   {phase === 'streaming' && <DraftStreamView steps={steps} />}
 *   {phase === 'done' && <DraftPreview result={result} />}
 *
 * Estados por step: 'pending' | 'running' | 'complete' | 'error'.
 *
 * El consumer NO debe llamar al api/agents directamente — este hook
 * encapsula:
 *   - llamada a streamSectionDraft
 *   - acumulación de eventos a estado React
 *   - AbortController
 *   - cleanup en unmount
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  type AgentMetrics,
  type Citation,
  type DraftStreamEvent,
  type ContextWarning,
  type LoadedContextSection,
  type ResolvedTokenInfo,
  type SectionDraftResult,
  type WaitingPayload,
  streamSectionDraft,
} from '../api/agents'
import { ApiError } from '../api/client'
import { useAccessToken } from '../auth/useAccessToken'
import { aiWaitingFrom } from '../lib/aiWaiting'

/** Steps que el SectionDraftAgent emite, en el orden esperado. */
export const STREAM_STEPS = [
  'plan',
  'retrieve',
  'generate',
  'critique',
  'finalize',
] as const

export type StreamStepName = (typeof STREAM_STEPS)[number]

export type StepStatus = 'pending' | 'running' | 'complete' | 'error'

export interface StepState {
  name: string
  status: StepStatus
  role: string | null
  modelId: string | null
  latencyMs: number
  inputTokens: number
  outputTokens: number
  cachedInputTokens: number
  /** Para `retrieve`: chunks/sources encontrados. */
  retrieve?: {
    chunksCount: number
    sourcesCount: number
    queriesCount: number
  }
  /** Para `critique`: si pasó al primer intento + textos reales del
   * critic (primeras 3 issues + suggestions, truncadas a 200 chars).
   * Permite diagnóstico en vivo cuando algo sale raro. */
  critique?: {
    passes: boolean
    issuesCount: number
    suggestionsCount: number
    issues: string[]
    suggestions: string[]
  }
}

export type DraftPhase = 'idle' | 'streaming' | 'done' | 'error'

export interface UseStreamDraftResult {
  phase: DraftPhase
  steps: StepState[]
  warnings: string[]
  /** Tokens {{section_id.value}} que el backend sustituyó. Vacío hasta
   * que llegue el evento `tokens_resolved` (justo antes del primer step).
   * Si el user no envió tokens, queda vacío toda la vida. */
  tokensResolved: ResolvedTokenInfo[]
  /** Warnings del resolver (token apunta a section_id inexistente,
   * field no soportado, etc.). Se muestran al user en panel diagnóstico. */
  tokenWarnings: string[]
  /** ADR-0002 Fase 10.f UX fix — warnings estructurados del resolver de
   * `context_sections`. Frontend localiza con `code` y opcionalmente
   * muestra el `ref`. Vacío si no hubo cross-refs ni problemas con intra. */
  contextWarnings: ContextWarning[]
  /** Secciones declaradas en `template.context_sections` cargadas como
   * contexto interno. Vacío si el template no las declaró o si todas
   * estaban vacías en el current_version. */
  contextSectionsLoaded: LoadedContextSection[]
  /** Espera del modelo en curso (latido `waiting`); null si hubo progreso
   * desde el último latido o el stream no está corriendo. */
  waiting: WaitingPayload | null
  result: SectionDraftResult | null
  error: { code?: string; message: string } | null
  start: (args: {
    artifactId: string
    sectionId: string
    sectionType?: string | null
    profileName?: string | null
    /** (Modo 2.1) Texto libre del user inyectado al system prompt. */
    userInstructions?: string | null
    /** (Modo 2.1) Contenido actual de la sección si el user marcó
     * "tomar como referencia". */
    currentContent?: unknown
    /** (Sprint AI-1.f) Path desde root al caller con índices de
     * instancias activas de repetibles. Permite resolver casos 2/3. */
    currentPath?: { section_id: string; index?: number }[]
    /** Doc 07 — versión target. Cuando el artifact tiene canon activo y
     * el user está editando v(N+1) draft, este id apunta a esa draft.
     * Sin este id el backend cae sobre `current_version_id` (caso
     * legacy v1 inicial). */
    versionId?: string | null
  }) => void
  abort: () => void
  reset: () => void
}

/** Estado inicial de los steps (antes de empezar). */
function initialSteps(): StepState[] {
  return STREAM_STEPS.map((name) => ({
    name,
    status: 'pending' as StepStatus,
    role: null,
    modelId: null,
    latencyMs: 0,
    inputTokens: 0,
    outputTokens: 0,
    cachedInputTokens: 0,
  }))
}

export function useStreamDraft(): UseStreamDraftResult {
  const { accessToken } = useAccessToken()
  const [phase, setPhase] = useState<DraftPhase>('idle')
  const [steps, setSteps] = useState<StepState[]>(initialSteps)
  const [warnings, setWarnings] = useState<string[]>([])
  const [tokensResolved, setTokensResolved] = useState<ResolvedTokenInfo[]>([])
  const [tokenWarnings, setTokenWarnings] = useState<string[]>([])
  const [contextWarnings, setContextWarnings] = useState<ContextWarning[]>([])
  const [contextSectionsLoaded, setContextSectionsLoaded] = useState<
    LoadedContextSection[]
  >([])
  const [waiting, setWaiting] = useState<WaitingPayload | null>(null)
  const [result, setResult] = useState<SectionDraftResult | null>(null)
  const [error, setError] = useState<UseStreamDraftResult['error']>(null)

  const abortRef = useRef<AbortController | null>(null)

  const reset = useCallback(() => {
    setPhase('idle')
    setSteps(initialSteps())
    setWarnings([])
    setTokensResolved([])
    setTokenWarnings([])
    setContextWarnings([])
    setContextSectionsLoaded([])
    setWaiting(null)
    setResult(null)
    setError(null)
  }, [])

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }, [])

  const start = useCallback(
    (args: {
      artifactId: string
      sectionId: string
      sectionType?: string | null
      profileName?: string | null
      userInstructions?: string | null
      currentContent?: unknown
      currentPath?: { section_id: string; index?: number }[]
      versionId?: string | null
    }) => {
      if (!accessToken) {
        setError({ message: 'no_access_token' })
        setPhase('error')
        return
      }
      // Reset previo (relanzar tras done o error).
      setPhase('streaming')
      setSteps(initialSteps())
      setWarnings([])
      setTokensResolved([])
      setTokenWarnings([])
      setContextSectionsLoaded([])
      setResult(null)
      setError(null)

      const ctrl = new AbortController()
      abortRef.current = ctrl

      void (async () => {
        try {
          for await (const evt of streamSectionDraft(
            accessToken,
            args.artifactId,
            args.sectionId,
            {
              profileName: args.profileName ?? null,
              sectionType: args.sectionType ?? null,
              userInstructions: args.userInstructions ?? null,
              currentContent: args.currentContent,
              currentPath: args.currentPath ?? [],
              versionId: args.versionId ?? null,
              signal: ctrl.signal,
            },
          )) {
            setWaiting(aiWaitingFrom(evt))
            applyEvent(
              setSteps,
              setWarnings,
              setTokensResolved,
              setTokenWarnings,
              setContextWarnings,
              setContextSectionsLoaded,
              evt,
            )
            if (evt.type === 'done') {
              setResult(evt.payload)
              setPhase('done')
              break
            }
            if (evt.type === 'error') {
              setError({
                ...(evt.payload.code !== undefined
                  ? { code: evt.payload.code }
                  : {}),
                message: evt.payload.message,
              })
              setPhase('error')
              break
            }
          }
        } catch (exc) {
          // Solo trata excepciones que NO son AbortError.
          if (ctrl.signal.aborted) {
            setPhase('idle')
            return
          }
          // Corte de red (`STREAM_INTERRUPTED`) o error HTTP previo al
          // stream: conservar el código para mostrar la copy traducida.
          const e = exc instanceof Error ? exc : new Error(String(exc))
          setError({
            ...(exc instanceof ApiError ? { code: exc.code } : {}),
            message: e.message,
          })
          setPhase('error')
        } finally {
          // Un `start` posterior ya pudo reemplazar el controller.
          if (abortRef.current === ctrl) abortRef.current = null
          setWaiting(null)
        }
      })()
    },
    [accessToken],
  )

  // Abort al desmontar — evita request fantasma cuando el modal cierra
  // a la mitad.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
    }
  }, [])

  // Memo para evitar new arrays en cada render — útil para el componente.
  const memoSteps = useMemo(() => steps, [steps])

  return {
    phase,
    steps: memoSteps,
    warnings,
    tokensResolved,
    tokenWarnings,
    contextWarnings,
    contextSectionsLoaded,
    waiting,
    result,
    error,
    start,
    abort,
    reset,
  }
}

/** Aplica un evento del stream al estado React. */
function applyEvent(
  setSteps: React.Dispatch<React.SetStateAction<StepState[]>>,
  setWarnings: React.Dispatch<React.SetStateAction<string[]>>,
  setTokensResolved: React.Dispatch<React.SetStateAction<ResolvedTokenInfo[]>>,
  setTokenWarnings: React.Dispatch<React.SetStateAction<string[]>>,
  setContextWarnings: React.Dispatch<React.SetStateAction<ContextWarning[]>>,
  setContextSectionsLoaded: React.Dispatch<
    React.SetStateAction<LoadedContextSection[]>
  >,
  evt: DraftStreamEvent,
): void {
  if (evt.type === 'tokens_resolved') {
    setTokensResolved(evt.payload.resolved)
    setTokenWarnings(evt.payload.warnings)
    setContextWarnings(evt.payload.context_warnings ?? [])
    return
  }
  if (evt.type === 'context_loaded') {
    setContextSectionsLoaded(evt.payload.sections)
    return
  }
  if (evt.type === 'step_start') {
    setSteps((prev) => updateStep(prev, evt.payload.step, (s) => ({
      ...s,
      status: 'running',
      role: evt.payload.role,
    })))
    return
  }
  if (evt.type === 'step_complete') {
    const p = evt.payload
    setSteps((prev) =>
      updateStep(prev, p.step, (s) => ({
        ...s,
        status: 'complete',
        role: p.role ?? s.role,
        modelId: p.model_id ?? s.modelId,
        latencyMs: p.latency_ms ?? s.latencyMs,
        inputTokens: p.input_tokens,
        outputTokens: p.output_tokens,
        cachedInputTokens: p.cached_input_tokens ?? 0,
      })),
    )
    return
  }
  if (evt.type === 'retrieve_done') {
    setSteps((prev) =>
      updateStep(prev, 'retrieve', (s) => ({
        ...s,
        retrieve: {
          chunksCount: evt.payload.chunks_count,
          sourcesCount: evt.payload.sources_count,
          queriesCount: evt.payload.queries_count,
        },
      })),
    )
    return
  }
  if (evt.type === 'critique_result') {
    setSteps((prev) =>
      updateStep(prev, 'critique', (s) => ({
        ...s,
        critique: {
          passes: evt.payload.passes,
          issuesCount: evt.payload.issues_count,
          suggestionsCount: evt.payload.suggestions_count,
          issues: evt.payload.issues ?? [],
          suggestions: evt.payload.suggestions ?? [],
        },
      })),
    )
    return
  }
  if (evt.type === 'warning') {
    setWarnings((prev) =>
      prev.includes(evt.payload.code) ? prev : [...prev, evt.payload.code],
    )
    return
  }
  // 'done' y 'error' los maneja el caller — necesita acceso al payload
  // completo y a `setPhase`.
}

export function updateStep(
  steps: StepState[],
  name: string,
  patch: (s: StepState) => StepState,
): StepState[] {
  let found = false
  const next = steps.map((s) => {
    if (s.name === name) {
      found = true
      return patch(s)
    }
    return s
  })
  // Un step que no está en la lista inicial (`generate_revise`, `revise`,
  // `ground`) ocurre antes de los que siguen pendientes: se inserta delante
  // del primero de ellos para que la lista refleje el orden real del flujo.
  if (!found) {
    const firstPending = next.findIndex((s) => s.status === 'pending')
    next.splice(
      firstPending === -1 ? next.length : firstPending,
      0,
      patch({
        name,
        status: 'running',
        role: null,
        modelId: null,
        latencyMs: 0,
        inputTokens: 0,
        outputTokens: 0,
        cachedInputTokens: 0,
      }),
    )
  }
  return next
}

// ─── Helpers de consumo ───────────────────────────────────────────────────
/** Tipos auxiliares re-exportados para el modal. */
export type {
  AgentMetrics,
  Citation,
  ContextWarning,
  LoadedContextSection,
  ResolvedTokenInfo,
  SectionDraftResult,
}
